// ============================================================================
// OMS sales-order list — paginated Ant table. Row click opens the drill-down.
// ============================================================================
import {
  CopyOutlined,
  ExportOutlined,
  InfoCircleOutlined,
  RedoOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Badge, Button, Empty, Input, Popconfirm, Space, Table, Tabs, Tag, Tooltip } from "antd";
import type { InputRef } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { OmsTab, OmsTabCounts, listSalesOrders, retrySalesOrders } from "../oms.api";
import { SalesOrderListRow } from "../types";
import { DeliveryStatusTag } from "../logistics/components/logistics_status_tag";
import { useIsAdmin } from "../../../custom_hooks/use_is_admin";
import { useRememberedTab } from "../remembered_tab";
import CopyText from "./copy_text";
import ExpressTag from "./express_tag";
import StatusTag from "./status_tag";
import UnloadingTag from "./unloading_tag";

const PAGE_SIZE = 50;

// Orders "Retry all" leaves alone — nothing on a closed order can be requeued. The backend skips
// them too; filtering here as well keeps the button's own count honest.
const DONE_STATUSES = ["completed", "cancelled"];

// SEARCH is a tab here, and it is the one the screen opens on by default — the overwhelmingly most
// common reason to come to this list is to find ONE order somebody already named. It is the only tab
// that is not a server predicate: it holds no cut of its own and issues no query at all until
// something is typed, so arriving costs nothing. See `remembered_tab.ts` for why it is the default
// and how the operator's own last choice takes over from it.
const SEARCH_TAB = "search";
type ListTab = OmsTab | typeof SEARCH_TAB;

// The board's cuts. Every one but Search is a SQL predicate server-side, so a tab switch paginates in
// the database — the `status` / `error_kind` dropdowns these replaced were derived in python, which
// meant loading EVERY order (with its allocations, stages, trucks, events and documents) to hand
// back a page of 50, and that cost grew with the order table for ever.
const TABS: { key: ListTab; label: string; hint: string }[] = [
  {
    key: SEARCH_TAB,
    label: "Search",
    hint: "Find one order by SO number, ENQ or client phone — across every order, whatever its state. Nothing loads until you type.",
  },
  { key: "all", label: "All orders", hint: "Every order, newest first" },
  {
    key: "undelivered",
    label: "Undelivered",
    hint: "Goods still owed to the customer — a delivery leg that is neither complete nor cancelled. Cancelled orders drop off on their own.",
  },
  {
    key: "delivered_open",
    label: "Delivered, still open",
    hint: "Every delivery leg done, but the paperwork or the money is not — an invoice, a customer payment or a return still outstanding",
  },
  {
    key: "delivered_not_invoiced",
    label: "Delivered, not invoiced",
    hint: "Delivered, but not one invoice reached Zoho — the bill could not be raised at all, whether it failed or was never attempted. Unlike \u201cDelivered, still open\u201d, raised-but-unpaid invoices are excluded.",
  },
  {
    key: "errors",
    label: "Critical errors",
    hint: "Real failures someone has to fix. Payment and settlement waits are excluded — those resolve themselves when the money lands, and otherwise bury the failures that do not.",
  },
];

const TAB_STORAGE_KEY = "oms.overview.tab";
const TAB_KEYS = TABS.map((t) => t.key);

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

// Column header with a hover explanation — the two status columns are otherwise
// indistinguishable to anyone who doesn't already know the data model.
const headerWithHelp = (label: string, help: string) => (
  <Tooltip title={help}>
    <span>
      {label} <InfoCircleOutlined style={{ color: "#bbb", fontSize: 12 }} />
    </span>
  </Tooltip>
);

// Open an order in a NEW browser tab. The dashboard is a work queue ops scans top-to-bottom, so
// opening the detail in place (and losing the scroll position / page / filters on the way back) is
// the wrong default — every way into an order opens a new tab instead.
const orderHref = (id: number) => `/oms/${id}`;
const openOrder = (id: number) => window.open(orderHref(id), "_blank", "noopener,noreferrer");

interface Props {
  // Optional selection hook (kept for API compatibility); the row itself opens a new tab.
  onSelect?: (salesOrderId: number) => void;
  selectedId?: number;
}

const SalesOrderListV1: React.FC<Props> = ({ selectedId }) => {
  const [rows, setRows] = useState<SalesOrderListRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useRememberedTab<ListTab>(TAB_STORAGE_KEY, TAB_KEYS, SEARCH_TAB);
  // On the Search tab the list is a blank slate until there is something to look for.
  const searching = tab === SEARCH_TAB;
  const [tabCounts, setTabCounts] = useState<OmsTabCounts | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // Strict admin ROLE — "Retry all" touches every order on screen, so it is deliberately narrower
  // than the per-order Retry (oms.retry, which tech holds too).
  const isAdmin = useIsAdmin();
  // Land the cursor in the box the operator came here to type in — including when they switch back
  // to the tab, which antd does without remounting the input (so `autoFocus` alone would not fire).
  const searchRef = useRef<InputRef>(null);
  useEffect(() => {
    if (searching) searchRef.current?.focus();
  }, [searching]);

  // Type-to-search. Without this the only way to reach an order is paging through
  // 50-row screens, which is the first thing ops hits on day one.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch((prev) => {
        const next = searchInput.trim();
        if (next !== prev) setPage(1);
        return next;
      });
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    // The whole point of the Search tab: no query, no request. Nothing is fetched, nothing is
    // counted, and the screen costs one render.
    if (searching && !search) {
      setRows([]);
      setCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await listSalesOrders({
      // Search is not a cut of the data — a lookup has to reach every order whatever its state,
      // which is exactly what `all` is. The tab only decides what the operator is looking AT.
      tab: searching ? "all" : (tab as OmsTab),
      search: search || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    });
    if (res) {
      setRows(res.results);
      setCount(res.count);
      // Not while searching: these counts come back scoped to the query, so writing them to the
      // badges would label the other tabs with "how many matched my search" — a number that reads
      // as the size of each cut and is not.
      if (!searching) setTabCounts(res.tab_counts ?? null);
    }
    setLoading(false);
  }, [tab, searching, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  // The unfinished orders on THIS page — the button's scope is what the operator can see.
  const retryableIds = rows.filter((r) => !DONE_STATUSES.includes(r.status)).map((r) => r.id);

  const onRetryAll = async () => {
    setRetrying(true);
    try {
      await retrySalesOrders(retryableIds);
      await load();
    } finally {
      setRetrying(false);
    }
  };

  const columns: ColumnsType<SalesOrderListRow> = [
    {
      // SO number and lead id were two columns holding the same string on most orders — the SO is
      // raised straight off the enquiry, so the pair only diverges when Zoho hasn't numbered the
      // order yet. One column now: the clickable id, with the lead id (and its tags) underneath
      // only when it actually differs.
      title: "SO / Lead ID",
      key: "so_lead",
      dataIndex: "zoho_salesorder_number",
      width: 230,
      fixed: "left",
      render: (num, row) => {
        const so = (num || "").toString().trim();
        const lead = (row.lead_id || "").toString().trim();
        const primary = so || `SO #${row.id}`;
        const showLead = !!lead && lead !== primary;
        const hasTags = row.mode === "express" || !!row.is_unloading;
        return (
          <Space direction="vertical" size={2} style={{ display: "flex" }}>
            <Space size={4} wrap>
              {/* A real anchor opening a new tab, so native right-click → "open in new tab" works
                  and a plain click doesn't double-fire with the row handler (both open the same
                  new tab anyway). */}
              <Link
                to={orderHref(row.id)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <strong>{primary}</strong>
              </Link>
              {/* Click-to-copy: the lead id is the handle operators paste into Zoho, WhatsApp and
                  search boxes all day, and retyping it off the screen is where transcription
                  errors come from. With no line of its own it rides beside the link as an icon. */}
              {!showLead && lead ? (
                <CopyText value={lead} label="Lead ID" style={{ color: "#bbb" }}>
                  <CopyOutlined />
                </CopyText>
              ) : null}
            </Space>
            {showLead || hasTags ? (
              <Space size={4} wrap>
                {showLead ? (
                  <CopyText value={lead} label="Lead ID">
                    <span style={{ color: "#666" }}>{lead}</span>
                  </CopyText>
                ) : null}
                <ExpressTag show={row.mode === "express"} />
                <UnloadingTag show={row.is_unloading} />
              </Space>
            ) : null}
          </Space>
        );
      },
    },
    { title: "Source", dataIndex: "order_source", width: 110, render: (v) => v || "—" },
    {
      title: "Client",
      dataIndex: "client_name",
      width: 140,
      // The name is the fastest way into the client's history from a stuck order — it opens the
      // same /client/:id screen the Clients list links to, in a new tab so the queue keeps its
      // place. Falls back to plain text when the row carries no client id.
      render: (v, row) => {
        if (!v) return "—";
        if (!row.client_id) return v;
        return (
          <Link
            to={`/client/${row.client_id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {v}
          </Link>
        );
      },
    },
    {
      title: headerWithHelp(
        "OMS Status",
        "How far we are with actually fulfilling this order — this is what OMS is tracking."
      ),
      dataIndex: "status",
      width: 140,
      render: (s, row) => (
        <StatusTag
          status={s}
          tooltip={row.error_message || row.hold_reason}
          errorKind={row.error_kind}
        />
      ),
    },
    {
      // Delivery progress on its own axis — where the goods physically are, independent of any
      // payment/invoice hold on the order. Rolled up across the order's legs (the least-advanced).
      title: headerWithHelp(
        "Delivery",
        "Where the physical delivery stands, independent of payment/invoicing. Rolled up across the order's legs — it reads 'delivered' only once every leg is."
      ),
      dataIndex: "delivery_status",
      width: 130,
      render: (v) => <DeliveryStatusTag status={v} />,
    },
    {
      title: headerWithHelp(
        "Order Status",
        "The state of the estimate / order in Zoho — set by sales, not by fulfilment."
      ),
      dataIndex: "estimate_status",
      width: 160,
      render: (v) => (v ? String(v).replace(/_/g, " ") : "—"),
    },
    {
      title: "Total",
      dataIndex: "total_estimate_amount",
      width: 130,
      align: "right",
      render: (v) => (v != null ? inr.format(Number(v)) : "—"),
    },
    { title: "Items", dataIndex: "estimate_item_count", width: 70, align: "right" },
    { title: "Allocations", dataIndex: "allocation_count", width: 100, align: "right" },
    {
      // A placed order with unsourced lines isn't visible in the fulfilment status — it just sits.
      // Flag it here so the list itself shows the wait.
      title: headerWithHelp(
        "Awaiting",
        "Lines with no lead time yet — unallocated and un-procured until procurement confirms availability (OMS → Availability)."
      ),
      dataIndex: "pending_availability_count",
      width: 100,
      align: "right",
      render: (n: number) =>
        n > 0 ? <Tag color="orange">{n}</Tag> : <span style={{ color: "#bbb" }}>—</span>,
    },
    { title: "Created by", dataIndex: "created_by", width: 120, render: (v) => v || "—" },
    { title: "Assigned to", dataIndex: "assigned_to", width: 120, render: (v) => v || "—" },
    {
      title: "Order placed",
      dataIndex: "order_placed_time",
      width: 160,
      render: (v) => (v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—"),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      width: 160,
      render: (v) => (v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—"),
    },
    {
      title: "Zoho",
      key: "zoho",
      width: 90,
      fixed: "right",
      align: "center",
      render: (_, row) =>
        row.zoho_url ? (
          <Button
            type="link"
            size="small"
            icon={<ExportOutlined />}
            href={row.zoho_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Tabs
        activeKey={tab}
        onChange={(k) => {
          setPage(1);
          setTab(k as ListTab);
        }}
        style={{ marginBottom: -8 }}
        items={TABS.map((t) => ({
          key: t.key,
          label: (
            <Tooltip title={t.hint}>
              <span>
                {t.label}
                {/* Search carries no count — it is not a set of orders, it is a question. */}
                {t.key !== SEARCH_TAB && tabCounts ? (
                  <Badge
                    count={tabCounts[t.key as OmsTab]}
                    showZero
                    overflowCount={9999}
                    style={{
                      marginLeft: 8,
                      backgroundColor:
                        t.key === "errors" && tabCounts.errors > 0 ? "#cf1322" : "#8c8c8c",
                    }}
                  />
                ) : null}
              </span>
            </Tooltip>
          ),
        }))}
      />
      <Space wrap align="end">
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Search</div>
          <Input.Search
            ref={searchRef}
            placeholder="SO number, ENQ or client phone…"
            allowClear
            style={{ width: 300 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={(v) => {
              setPage(1);
              setSearch(v.trim());
            }}
          />
        </div>
        {search && (
          <Button
            onClick={() => {
              setPage(1);
              setSearchInput("");
              setSearch("");
            }}
          >
            Clear search
          </Button>
        )}
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
        {isAdmin ? (
          <Popconfirm
            title="Retry all on this page"
            description={`Requeue every failed document and booking on the ${retryableIds.length} unfinished order(s) shown. Completed and cancelled orders are skipped.`}
            okText="Retry all"
            onConfirm={onRetryAll}
            disabled={retrying || !retryableIds.length}
          >
            <Button
              icon={<RedoOutlined />}
              loading={retrying}
              disabled={!retryableIds.length}
            >
              Retry all ({retryableIds.length})
            </Button>
          </Popconfirm>
        ) : null}
      </Space>
      <div style={{ color: "#8c8c8c", fontSize: 13, marginTop: -8 }}>
        {searching && !search
          ? "Type an SO number, ENQ or client phone above to find an order"
          : count === 0
            ? "No orders match"
            : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, count)} of ${count} order${count === 1 ? "" : "s"}`}
        {search ? ` for “${search}”` : ""}
      </div>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: "max-content" }}
        locale={
          searching && !search
            ? {
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Search for an order to begin"
                  />
                ),
              }
            : undefined
        }
        onRow={(record) => ({
          // Left-click and middle-click both open the order in a new tab, so the dashboard keeps its
          // place. (The id cells are real links too, so native right-click → "open in new tab" works.)
          onClick: () => openOrder(record.id),
          onAuxClick: (e) => {
            if (e.button === 1) openOrder(record.id);
          },
          style: {
            cursor: "pointer",
            background: record.id === selectedId ? "#e6f7f9" : undefined,
          },
        })}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: count,
          showSizeChanger: false,
          onChange: setPage,
        }}
      />
    </Space>
  );
};

export default SalesOrderListV1;
